import { makeStyles } from "@material-ui/core/styles";
import { Exercises } from "app-interfaces";
import clsx from "clsx";
import React from "react";

import { useStateValue } from "../../../config/AppContext";
import { getSuperSetFormDataInputElement } from "../../Modals/SuperSetForm";
import Icons from "../../Utilities/Icons";

export type SelectedExercises = { exercise_id: string }[] | null;

interface SelectExerciseListItemProps {
  exercise: Exercises.IExercise;
  selected: SelectedExercises;
  onSelectionChange: (selectedExercises: SelectedExercises) => void;
}

const useStyles = makeStyles((theme) => ({
  selectedExerciseItem: {
    display: "flex",
    flexDirection: "row",
    padding: "8px",
    marginBottom: "5px",
    cursor: "pointer",
  },
  exerciseImageContainer: {
    width: "76px",
  },
  exerciseImage: {
    width: "100%",
    height: 65,
  },
  exerciseInfoBlock: {
    textTransform: "capitalize",
    flexGrow: 1,
    padding: "0 1em",
  },
  exerciseName: {
    margin: 0,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  exerciseType: {
    fontSize: "14px",
  },
  exercisePrimaryFocus: {
    fontSize: "12px",
  },
  exercisePrimaryFocusIcon: {
    borderRadius: "50%",
    backgroundColor: "var(--text-primary)",
    height: "15px",
    marginRight: "5px",
    marginBottom: "2px",
  },
}));

const SelectExerciseListItem: React.FC<SelectExerciseListItemProps> = ({
  exercise,
  selected,
  onSelectionChange,
}) => {
  const { id, photo_start, name, type, focus_prim } = exercise;
  const { ic_placeholder, ic_info } = Icons;

  const classes = useStyles();
  const { catalog } = useStateValue();

  React.useEffect(() => {
    const superSetInputElem = getSuperSetFormDataInputElement();
    if (superSetInputElem && !selected)
      return onSelectionChange(JSON.parse(superSetInputElem.value));
  }, [selected, onSelectionChange]);

  const handleSelect = (e: React.MouseEvent) => {
    const id = e.currentTarget.id;
    const superSetInputElem = getSuperSetFormDataInputElement();
    if (!superSetInputElem) return;
    if (selected) {
      const existingEntry = selected.find((el) => el.exercise_id === id);
      const newSelected = existingEntry
        ? selected.filter((el) => el.exercise_id !== existingEntry.exercise_id)
        : [...selected, { exercise_id: id }];
      superSetInputElem.value = JSON.stringify(newSelected);
      return onSelectionChange(newSelected);
    }
    superSetInputElem.value = JSON.stringify([{ exercise_id: id }]);
    return onSelectionChange([{ exercise_id: id }]);
  };

  const isSelected = selected && selected.find((el) => el.exercise_id === id);

  return (
    <div
      key={id}
      id={id}
      onClick={handleSelect}
      className={clsx("card", classes.selectedExerciseItem, { active: isSelected })}
    >
      <div className={classes.exerciseImageContainer}>
        <img
          src={photo_start ? photo_start.medium : ic_placeholder}
          alt="exercises"
          className={classes.exerciseImage}
          onError={(event) => {
            event.currentTarget.src = ic_placeholder;
            event.currentTarget.onerror = null;
            return true;
          }}
        />
      </div>
      <div className={classes.exerciseInfoBlock}>
        <p className={classes.exerciseName}>{name}</p>
        <p className={classes.exerciseType}>
          {catalog && catalog.typesDisplay(type.toString()).toLowerCase()}
        </p>
        <p className={classes.exercisePrimaryFocus}>
          <img alt="info" src={ic_info} className={classes.exercisePrimaryFocusIcon} />
          {focus_prim ? catalog.typesDisplay(focus_prim.toString()).toLowerCase() : "None"}
        </p>
      </div>
      <span className={clsx("selected", { active: isSelected })}>
        {isSelected && selected!.findIndex((el) => el.exercise_id === id) + 1}
      </span>
    </div>
  );
};

export default SelectExerciseListItem;
